"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

/**
 * Coordinates the single fixed `bottom-0` slot on mobile. Several components
 * independently render a `fixed inset-x-0 bottom-0 z-40 ... md:hidden`/`lg:hidden`
 * bar — the job-detail Apply CTA, the pricing page CTA, and the monetization
 * engine's `mobile_sticky` ad bar (mounted globally in the public layout so it
 * can appear on ANY page). With no coordination they stack at the identical
 * position/z-index and visually cover each other — on mobile job-detail pages
 * the ad bar (mounted after the page content in the DOM, so it paints on top)
 * covered the primary Apply button once `mobile_sticky` went live. A
 * page-owned conversion CTA always outranks the ad: it claims the slot for as
 * long as it is mounted, and `MobileStickyAd` checks the claim before it will
 * render (or fetch an offer at all). `PostFeedbackWidget` and
 * `FeedDiscoveryNudge` also honour the claim now — the same ambient-nag-over-CTA
 * bug recurred on mobile job pages (2026-07).
 */

/**
 * Two kinds of claim (2026-09-28, owner rule "the ad is always there"):
 *
 * - `blocking` — the page-owned bar must own the bottom edge ALONE (pricing
 *   CTA, pricing success). The ad bar yields entirely.
 * - `stackable` — the page-owned bar keeps the bottom edge, but the ad bar may
 *   sit directly ABOVE it (job Apply bar, Hunt chat composer). The claimant
 *   stays in the thumb zone and is never covered; the ad moves up instead of
 *   disappearing. Before this split every claim was blocking, which is why the
 *   bottom ad vanished on every mobile job page (41% of pageviews).
 *
 * Every ambient bar other than the ad (toasts, nudges, the extension banner)
 * still yields to BOTH kinds — `useStickyFooterClaimed()` with no options
 * keeps its original "anything claims the slot" meaning, so none of them can
 * start covering the Apply button because the ad learned to stack.
 */
export type StickyFooterClaimKind = "blocking" | "stackable";

type Ctx = {
	blocking: number;
	stackable: number;
	claim: (kind: StickyFooterClaimKind) => void;
	release: (kind: StickyFooterClaimKind) => void;
};
const StickyFooterContext = createContext<Ctx | null>(null);

export function StickyFooterClaimProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [counts, setCounts] = useState({ blocking: 0, stackable: 0 });
	const claim = useCallback(
		(kind: StickyFooterClaimKind) =>
			setCounts((c) => ({ ...c, [kind]: c[kind] + 1 })),
		[],
	);
	const release = useCallback(
		(kind: StickyFooterClaimKind) =>
			setCounts((c) => ({ ...c, [kind]: Math.max(0, c[kind] - 1) })),
		[],
	);
	const value = useMemo(
		() => ({
			blocking: counts.blocking,
			stackable: counts.stackable,
			claim,
			release,
		}),
		[counts, claim, release],
	);
	return (
		<StickyFooterContext.Provider value={value}>
			{children}
		</StickyFooterContext.Provider>
	);
}

/**
 * Call from a page-owned fixed bottom-0 bar for as long as it is actually
 * rendering that bar. Pass `active=false` (or omit calling on a branch that
 * renders nothing) when the bar isn't shown.
 *
 * `visibleQuery` is the media query at which the bar is actually VISIBLE, and
 * must be passed by any bar hidden at a CSS breakpoint.
 *
 * WHY (2026-09-07). `active` answers "is this bar mounted?", which is not the
 * same question as "is this bar occupying the slot?". A `lg:hidden` bar still
 * MOUNTS on a desktop viewport — only CSS hides it — so it claimed a slot it
 * did not occupy, on every screen, and all seven consumers of
 * `useStickyFooterClaimed()` politely yielded to a bar that was not there.
 * Measured: the paid apply-moment offer (`apply-moment-cv-offer`) took this
 * guard in #5009 and its impressions went 8 -> 5 -> 0, then stayed at 0 across
 * 21 apply clicks in 26 hours, while the flag sat active at 100%.
 *
 * The fallback when `matchMedia` is unavailable (jsdom, ancient browsers) is to
 * CLAIM, and the initial state before the effect runs is also CLAIMED. Both
 * directions are deliberate and follow the priority this file already states:
 * a missed sale is recoverable, a covered Apply button is not.
 */
export function useClaimStickyFooter(
	active: boolean,
	visibleQuery?: string,
	opts?: {
		/** Stackable claim: the ad bar may sit directly above this bar instead
		 * of hiding. Omit (blocking) for anything that must own the edge alone. */
		allowAdAbove?: boolean;
	},
): void {
	const ctx = useContext(StickyFooterContext);
	// Starts true so a bar that IS visible never yields the slot for a frame.
	const [visible, setVisible] = useState(true);

	useEffect(() => {
		if (!visibleQuery) {
			setVisible(true);
			return;
		}
		if (typeof window === "undefined" || !window.matchMedia) {
			setVisible(true);
			return;
		}
		const mq = window.matchMedia(visibleQuery);
		const sync = () => setVisible(mq.matches);
		sync();
		mq.addEventListener("change", sync);
		return () => mq.removeEventListener("change", sync);
	}, [visibleQuery]);

	const kind: StickyFooterClaimKind = opts?.allowAdAbove
		? "stackable"
		: "blocking";
	// Depend on the stable callbacks, not `ctx`: the context value changes on
	// every claim, and re-running this effect would release + re-claim forever.
	const claim = ctx?.claim;
	const release = ctx?.release;
	useEffect(() => {
		if (!claim || !release || !active || !visible) return;
		claim(kind);
		return () => release(kind);
	}, [claim, release, active, visible, kind]);
}

/**
 * The viewport where the job-detail mobile apply bar is actually on screen.
 * Mirrors its `lg:hidden` class — Tailwind `lg` is 1024px, so the bar exists
 * below 1024px and nowhere else. Exported so the bar and this contract cannot
 * drift apart silently.
 */
export const MOBILE_APPLY_BAR_VISIBLE_QUERY = "(max-width: 1023.98px)";

/**
 * Whether a page-owned sticky footer currently claims the bottom slot.
 *
 * Default: ANY claim, blocking or stackable — the meaning every ambient bar
 * (toasts, nudges, extension banner) relies on to stay off the Apply button.
 * `{ blockingOnly: true }` is for the ad bar alone: it only hides for a
 * blocking claim, and stacks above a stackable one (`useStickyFooterStacked`).
 */
export function useStickyFooterClaimed(opts?: {
	blockingOnly?: boolean;
}): boolean {
	const ctx = useContext(StickyFooterContext);
	if (!ctx) return false;
	return opts?.blockingOnly
		? ctx.blocking > 0
		: ctx.blocking + ctx.stackable > 0;
}

/**
 * True when the ad bar should render STACKED above a page-owned bar: at least
 * one stackable claim and no blocking claim (a blocking claim hides the ad).
 */
export function useStickyFooterStacked(): boolean {
	const ctx = useContext(StickyFooterContext);
	if (!ctx) return false;
	return ctx.blocking === 0 && ctx.stackable > 0;
}
